import { AlertCircle } from "lucide-react";
import { Banner, Card } from "@/components/ds";
import { Skeleton } from "@/components/ui/skeleton";
import { EntraSyncStatusCard } from "@/components/corporate/organizations/EntraSyncStatusCard";
import { AllowedDomainsEditor } from "@/components/corporate/organizations/access/AllowedDomainsEditor";
import { useOrgEntraSyncStatus } from "@/hooks/useOrgEntraSyncStatus";

interface OrgIntegrationsTabProps {
  organizationId: string;
}

/**
 * Integrations: Microsoft sign-in status (read-only, incl. the safety-catch alert — W7) and the
 * allowed email domains, locked for a Microsoft company with the reason (E1). `entra-sync-status`
 * is the only source of "signs in with Microsoft"; until it is known the domains stay locked.
 * Changing or turning off Microsoft sign-in is a later plan (cms-org-entra).
 */
export function OrgIntegrationsTab({ organizationId }: OrgIntegrationsTabProps) {
  const { data: status, isLoading, isError } = useOrgEntraSyncStatus(organizationId);
  const entraCheck = isError ? "failed" : isLoading ? "loading" : undefined;

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="org-integrations">
      <div className="space-y-3">
        <h2 className="text-[15px] font-semibold text-ds-ink">Microsoft sign-in</h2>
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : isError ? (
          <Banner tone="err" icon={AlertCircle}>
            Couldn&apos;t check Microsoft sign-in. Refresh the page to try again.
          </Banner>
        ) : status?.isEnabled ? (
          <EntraSyncStatusCard status={status} />
        ) : (
          <Card className="px-5 py-4">
            <p className="text-[13.5px] text-ds-ink2">Not connected. Employees sign in with an email code.</p>
          </Card>
        )}
      </div>
      <div className="space-y-3">
        {/* The editor carries its own "Allowed email domains" heading. */}
        <Card className="px-5 py-4 lg:mt-[33px]">
          <AllowedDomainsEditor
            organizationId={organizationId}
            hasEntraIntegration={!!status?.isEnabled}
            entraCheck={entraCheck}
          />
        </Card>
      </div>
    </div>
  );
}

export default OrgIntegrationsTab;
